<template>
  <div class="ota-page" v-if="permissions.manage">
    <div class="ota-heading">
      <div>
        <h2>
          <el-icon><UploadFilled /></el-icon>OTA 升级管理
        </h2>
        <p>管理 P4M 固件版本，选择设备分批升级并查看结果</p>
      </div>
      <el-button :icon="Refresh" :loading="loading" @click="load"
        >刷新</el-button
      >
    </div>
    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      :closable="false"
      class="ota-notice"
    />
    <div class="ota-cards">
      <div v-for="card in cards" :key="card.label" class="ota-card">
        <span>{{ card.label }}</span
        ><strong>{{ card.value }}</strong>
      </div>
    </div>
    <div class="ota-panel" v-loading="loading">
      <el-tabs v-model="activeTab">
        <el-tab-pane label="固件版本" name="releases">
          <div class="ota-toolbar">
            <span
              >发布后的版本文件不可覆盖；每次修改固件都使用新的版本编号。</span
            >
            <el-button
              type="primary"
              :disabled="!readyToWrite"
              @click="openUpload"
              >上传固件</el-button
            >
          </div>
          <el-alert
            v-if="!settings.storageConfigured"
            title="固件存储目录尚未配置，上传和发布暂不可用"
            type="info"
            :closable="false"
          />
          <el-table
            :data="releases"
            stripe
            empty-text="还没有固件版本。收到硬件提供的固件和版本文件后，在这里上传。"
          >
            <el-table-column
              prop="releaseId"
              label="版本编号"
              min-width="195"
            />
            <el-table-column prop="appVersion" label="固件版本" width="105" />
            <el-table-column label="文件大小" width="105"
              ><template #default="{ row }"
                >{{
                  (Number(row.imageSize) / 1024 / 1024).toFixed(2)
                }}
                MiB</template
              ></el-table-column
            >
            <el-table-column label="状态" min-width="115"
              ><template #default="{ row }"
                ><el-tag :type="otaTone(row.state)">{{
                  otaLabel(row.state)
                }}</el-tag></template
              ></el-table-column
            >
            <el-table-column label="上传时间" min-width="165"
              ><template #default="{ row }">{{
                otaTime(row.createdAt)
              }}</template></el-table-column
            >
            <el-table-column label="操作" min-width="265" fixed="right">
              <template #default="{ row }">
                <el-button link type="primary" @click="viewRelease(row)"
                  >详情</el-button
                >
                <template v-if="permissions.operate">
                  <el-button
                    v-if="row.state === 'DRAFT'"
                    link
                    type="primary"
                    :disabled="busy"
                    @click="releaseAction(row, 'approve')"
                    >审核</el-button
                  >
                  <el-button
                    v-if="row.state === 'APPROVED'"
                    link
                    type="success"
                    :disabled="busy"
                    @click="releaseAction(row, 'publish')"
                    >发布</el-button
                  >
                  <el-button
                    v-if="
                      ['DRAFT', 'APPROVED', 'PUBLISHED'].includes(row.state)
                    "
                    link
                    type="danger"
                    :disabled="busy"
                    @click="releaseAction(row, 'withdraw')"
                    >撤回</el-button
                  >
                </template>
              </template>
            </el-table-column>
          </el-table>
          <p class="ota-footnote">
            {{
              settings.preview
                ? "预览中的“发布”只生成本地文件，不代表公网已存在该版本。"
                : "发布清单后才可创建任务；开始升级前，服务器会再次检查公网文件与校验值。"
            }}
            最多展示最近 200 个版本。
          </p>
        </el-tab-pane>
        <el-tab-pane label="升级任务" name="tasks">
          <div class="ota-toolbar">
            <span>每批成功后需人工继续。暂停只影响尚未下发的设备。</span
            ><el-button
              type="primary"
              :disabled="!readyToWrite || !published.length"
              @click="openTask"
              >创建升级任务</el-button
            >
          </div>
          <el-table
            :data="deployments"
            stripe
            empty-text="暂无升级任务，请先上传、审核并发布固件版本。"
          >
            <el-table-column
              prop="releaseId"
              label="目标版本编号"
              min-width="200"
            />
            <el-table-column label="每批设备数" width="110"
              ><template #default="{ row }"
                >{{ row.batchSize }} 台</template
              ></el-table-column
            >
            <el-table-column label="状态" min-width="140"
              ><template #default="{ row }"
                ><el-tag :type="otaTone(row.state)">{{
                  otaLabel(row.state)
                }}</el-tag></template
              ></el-table-column
            >
            <el-table-column
              prop="reason"
              label="说明"
              min-width="220"
              show-overflow-tooltip
            />
            <el-table-column label="创建时间" min-width="165"
              ><template #default="{ row }">{{
                otaTime(row.createdAt)
              }}</template></el-table-column
            >
            <el-table-column label="操作" min-width="210" fixed="right"
              ><template #default="{ row }">
                <el-button link type="primary" @click="viewTask(row)"
                  >查看进度</el-button
                >
                <el-button
                  v-if="
                    permissions.operate &&
                    ['DRAFT', 'PAUSED', 'AWAITING_CONTINUE'].includes(row.state)
                  "
                  link
                  type="success"
                  :disabled="busy || !canStartOta(row, settings, role)"
                  @click="startTask(row)"
                  >{{
                    row.state === "DRAFT" ? "开始升级" : "继续下一批"
                  }}</el-button
                >
                <el-button
                  v-if="permissions.operate && row.state !== 'COMPLETED'"
                  link
                  type="warning"
                  :disabled="busy"
                  @click="pauseTask(row)"
                  >暂停</el-button
                >
              </template></el-table-column
            >
          </el-table>
          <p class="ota-footnote">
            “等待重启”不是升级成功。只有设备回报“已确认成功”，才计入完成。结果未确认时不会自动重发。
          </p>
        </el-tab-pane>
        <el-tab-pane label="设备条件" name="devices">
          <div class="ota-toolbar">
            <el-input
              v-model="deviceKeyword"
              placeholder="按机器码或机器名搜索"
              clearable
              style="max-width: 300px"
              @keyup.enter="loadDevices"
            /><el-button :loading="deviceLoading" @click="loadDevices"
              >搜索</el-button
            >
          </div>
          <el-table
            :data="deviceRows"
            stripe
            v-loading="deviceLoading"
            empty-text="未找到设备"
          >
            <el-table-column
              prop="machineCode"
              label="机器码"
              min-width="155"
            />
            <el-table-column prop="deviceId" label="设备编号" width="100" />
            <el-table-column prop="deviceName" label="机器名" min-width="130" />
            <el-table-column label="已登记版本" width="115"
              ><template #default="{ row }">{{
                row.readiness.currentVersion || "未登记"
              }}</template></el-table-column
            >
            <el-table-column label="最近状态" min-width="150"
              ><template #default="{ row }">{{
                otaLabel(row.snapshot.phase)
              }}</template></el-table-column
            >
            <el-table-column label="实时状态时间" min-width="170"
              ><template #default="{ row }">{{
                otaTime(row.lastLiveMs)
              }}</template></el-table-column
            >
            <el-table-column label="条件登记" min-width="130"
              ><template #default="{ row }"
                ><el-tag :type="row.verifiedAt ? 'success' : 'info'">{{
                  row.verifiedAt ? "已登记，升级前复核" : "等待硬件确认"
                }}</el-tag></template
              ></el-table-column
            >
            <el-table-column label="操作" min-width="155"
              ><template #default="{ row }"
                ><el-button
                  v-if="permissions.operate"
                  link
                  type="primary"
                  @click="editReadiness(row)"
                  >登记条件</el-button
                ><el-button
                  link
                  type="primary"
                  @click="viewEvents(row.deviceId)"
                  >设备记录</el-button
                ></template
              ></el-table-column
            >
          </el-table>
          <p class="ota-footnote">
            普通在线状态和历史保留消息不能证明设备已具备 OTA
            条件。硬件信息由超级管理员依据实际测试登记，升级前还会检查近 10
            分钟的实时 OTA 上报。最多显示 200 台，可按机器码搜索。
          </p>
        </el-tab-pane>
        <el-tab-pane label="操作记录" name="audit">
          <el-table :data="audit" stripe empty-text="暂无 OTA 操作记录">
            <el-table-column label="时间" min-width="170"
              ><template #default="{ row }">{{
                otaTime(row.createdAt)
              }}</template></el-table-column
            >
            <el-table-column label="操作人 ID" width="125"
              ><template #default="{ row }">{{
                Number(row.operatorId) === 0 ? "系统" : row.operatorId
              }}</template></el-table-column
            >
            <el-table-column label="操作" min-width="130"
              ><template #default="{ row }">{{
                auditLabel(row.action)
              }}</template></el-table-column
            >
            <el-table-column
              prop="objectId"
              label="关联编号"
              min-width="205"
              show-overflow-tooltip
            />
            <el-table-column
              prop="detail"
              label="说明"
              min-width="300"
              show-overflow-tooltip
            />
          </el-table>
          <p class="ota-footnote">
            保留最近 200 条操作记录；完整记录保存在后台数据库中。
          </p>
        </el-tab-pane>
      </el-tabs>
    </div>
    <el-dialog
      v-model="uploadVisible"
      title="上传实验室联调固件"
      width="640px"
      :close-on-click-modal="false"
    >
      <el-form label-width="115px" @submit.prevent>
        <el-form-item label="版本编号" required
          ><el-input
            v-model="uploadForm.releaseId"
            maxlength="48"
            placeholder="例如 p4m-1.0.1-lab1；每次发布使用新编号"
        /></el-form-item>
        <el-form-item label="当前基线版本" required
          ><el-input
            v-model="uploadForm.baselineVersion"
            placeholder="例如 1.0.0；目标版本必须更高"
        /></el-form-item>
        <el-form-item label="构建 Git 提交" required
          ><el-input
            v-model="uploadForm.gitCommit"
            maxlength="40"
            placeholder="硬件提供的真实提交值，12至40位小写十六进制"
        /></el-form-item>
        <el-form-item label="应用固件" required
          ><input
            aria-label="选择应用固件"
            type="file"
            accept=".bin"
            @change="chooseFile($event, 'firmware')"
        /></el-form-item>
        <el-form-item label="对应版本文件" required
          ><input
            aria-label="选择版本文件"
            type="file"
            accept=".txt"
            @change="chooseFile($event, 'versionFile')"
        /></el-form-item>
        <el-form-item label="发布说明" required
          ><el-input
            v-model="uploadForm.notes"
            type="textarea"
            :rows="3"
            maxlength="2000"
            show-word-limit
            placeholder="填写固件来源、修改内容和已有测试结果"
        /></el-form-item>
      </el-form>
      <p class="ota-footnote">
        仅接收 wifi_station.bin 与 version.txt。后台自动检查芯片、版本、5 MiB
        容量和 SHA256；无需手工填写校验值。
      </p>
      <template #footer
        ><el-button @click="uploadVisible = false" :disabled="busy"
          >取消</el-button
        ><el-button type="primary" :loading="busy" @click="submitUpload"
          >校验并保存草稿</el-button
        ></template
      >
    </el-dialog>
    <el-dialog v-model="releaseVisible" title="固件版本详情" width="760px">
      <el-descriptions v-if="selectedRelease" :column="1" border>
        <el-descriptions-item label="版本编号">{{
          selectedRelease.releaseId
        }}</el-descriptions-item>
        <el-descriptions-item label="固件版本">{{
          selectedRelease.appVersion
        }}</el-descriptions-item>
        <el-descriptions-item label="Git 提交">{{
          selectedRelease.gitCommit
        }}</el-descriptions-item>
        <el-descriptions-item label="SHA256"
          ><span class="ota-wrap">{{
            selectedRelease.sha256
          }}</span></el-descriptions-item
        >
        <el-descriptions-item label="发布说明">{{
          selectedRelease.notes
        }}</el-descriptions-item>
        <el-descriptions-item label="升级清单基地址">{{
          settings.manifestBaseUrl
        }}</el-descriptions-item>
      </el-descriptions>
      <p>
        设备升级清单{{ settings.preview ? "（预览内容，未发布到公网）" : "" }}
      </p>
      <pre class="ota-json">{{ manifestText }}</pre>
    </el-dialog>
    <el-dialog
      v-model="taskVisible"
      title="创建升级任务"
      width="850px"
      :close-on-click-modal="false"
    >
      <el-form label-width="110px"
        ><el-form-item label="目标版本" required
          ><el-select
            v-model="taskForm.releaseId"
            placeholder="选择已发布版本"
            style="width: 100%"
            @change="loadCandidates"
            ><el-option
              v-for="release in published"
              :key="release.releaseId"
              :label="`${release.releaseId} · ${release.appVersion}`"
              :value="release.releaseId" /></el-select></el-form-item
        ><el-form-item label="每批设备数"
          ><el-input-number
            v-model="taskForm.batchSize"
            :min="1"
            :max="10"
          /><span class="ota-inline-note"
            >首批建议 1 台，每批完成后人工继续</span
          ></el-form-item
        ></el-form
      >
      <div class="ota-toolbar">
        <el-input
          v-model="candidateKeyword"
          placeholder="按机器码或机器名搜索"
          @keyup.enter="loadCandidates"
        /><el-button @click="loadCandidates">搜索</el-button>
      </div>
      <el-table
        ref="candidateTable"
        :data="candidates"
        row-key="deviceId"
        max-height="320"
        v-loading="candidateLoading"
        @selection-change="selectCandidates"
        ><el-table-column type="selection" width="45" /><el-table-column
          prop="machineCode"
          label="机器码"
          width="155"
        /><el-table-column
          prop="deviceName"
          label="机器名"
          min-width="110"
        /><el-table-column label="升级前需处理" min-width="350"
          ><template #default="{ row }"
            ><span :class="row.blockers?.length ? 'ota-blocker' : 'ota-good'">{{
              row.blockers?.length
                ? row.blockers.join("；")
                : "当前条件通过，开始前会再次检查"
            }}</span></template
          ></el-table-column
        ></el-table
      >
      <p class="ota-footnote">
        已选择 {{ taskForm.deviceIds.length }} 台（最多 50
        台）。可先保存待准备的任务，条件未通过时不会发送升级命令。切换版本或搜索会清空选择。
      </p>
      <template #footer
        ><el-button @click="taskVisible = false" :disabled="busy"
          >取消</el-button
        ><el-button
          type="primary"
          :disabled="
            !taskForm.deviceIds.length || taskForm.deviceIds.length > 50
          "
          :loading="busy"
          @click="submitTask"
          >保存任务，不立即升级</el-button
        ></template
      >
    </el-dialog>
    <el-dialog v-model="detailVisible" title="设备升级进度" width="1000px">
      <template v-if="taskDetail"
        ><div class="ota-toolbar">
          <span
            >{{ taskDetail.releaseId }} · {{ otaLabel(taskDetail.state) }}</span
          ><el-button @click="refreshTask">刷新进度</el-button>
        </div>
        <el-alert
          v-if="taskDetail.reason"
          :title="taskDetail.reason"
          type="info"
          :closable="false"
        />
        <el-table :data="taskDetail.tasks" empty-text="暂无设备"
          ><el-table-column
            prop="machineCode"
            label="机器码"
            width="155"
          /><el-table-column label="状态" min-width="185"
            ><template #default="{ row }"
              ><el-tag :type="otaTone(row.phase)">{{
                otaLabel(row.phase)
              }}</el-tag></template
            ></el-table-column
          ><el-table-column label="进度" min-width="130"
            ><template #default="{ row }"
              ><el-progress
                :percentage="
                  Number(row.progress)
                " /></template></el-table-column
          ><el-table-column
            prop="attempts"
            label="尝试"
            width="65"
          /><el-table-column
            prop="errorCode"
            label="错误码"
            width="80"
          /><el-table-column
            prop="partitionName"
            label="分区"
            width="90"
          /><el-table-column
            prop="detail"
            label="设备说明"
            min-width="200"
            show-overflow-tooltip
          /><el-table-column label="记录" width="80"
            ><template #default="{ row }"
              ><el-button link type="primary" @click="viewEvents(row.deviceId)"
                >查看</el-button
              ></template
            ></el-table-column
          ></el-table
        >
        <p class="ota-footnote">
          每 10
          秒自动刷新。发送结果不明或状态超时的设备需先核实，系统不会自动重发或将“等待重启”计为成功。
        </p>
      </template>
    </el-dialog>
    <el-dialog
      v-model="readinessVisible"
      :title="`登记设备条件 · ${readinessId}`"
      width="650px"
      :close-on-click-modal="false"
    >
      <el-form label-width="135px"
        ><el-form-item label="当前固件版本" required
          ><el-input
            v-model="readinessForm.currentVersion"
            placeholder="由硬件核实，如 1.0.0" /></el-form-item
        ><el-form-item label="P4 硬件版本" required
          ><el-input
            v-model="readinessForm.boardRevision"
            placeholder="本轮要求 3.2" /></el-form-item
        ><el-form-item label="Flash 容量 MiB"
          ><el-input-number
            v-model="readinessForm.flashMiB"
            :min="1"
            :max="128" /></el-form-item
        ><el-form-item label="C6 协议版本" required
          ><el-input
            v-model="readinessForm.c6Protocol"
            placeholder="本轮要求 0.0" /></el-form-item
        ><el-form-item label="分区 / 配置版本"
          ><el-input-number
            v-model="readinessForm.partitionSchema"
            :min="1"
            :max="100" /><span class="ota-inline-note">/</span
          ><el-input-number
            v-model="readinessForm.configSchema"
            :min="1"
            :max="100" /></el-form-item
        ><el-form-item label="唯一机器码"
          ><el-checkbox v-model="readinessForm.uniqueIdConfirmed"
            >已核实该机器码仅对应这一台设备</el-checkbox
          ></el-form-item
        ><el-form-item label="设备基线"
          ><el-checkbox v-model="readinessForm.baselineReady"
            >已配置升级地址，并处理 C6 和启动自检问题</el-checkbox
          ></el-form-item
        ><el-form-item label="核实依据" required
          ><el-input
            v-model="readinessForm.notes"
            type="textarea"
            maxlength="500"
            :rows="3"
            placeholder="记录硬件确认人、测试时间和依据" /></el-form-item
      ></el-form>
      <template #footer
        ><el-button @click="readinessVisible = false">取消</el-button
        ><el-button type="primary" :loading="busy" @click="submitReadiness"
          >保存登记</el-button
        ></template
      >
    </el-dialog>
    <el-dialog
      v-model="eventsVisible"
      :title="`设备 OTA 记录 · ${eventsDevice}`"
      width="820px"
      ><el-empty
        v-if="!eventRows.length"
        description="还没有收到该设备的 OTA 状态"
      /><el-collapse v-else
        ><el-collapse-item
          v-for="event in eventRows"
          :key="event.id"
          :name="event.id"
          ><template #title
            >{{ otaTime(event.receivedAt) }} · {{ otaLabel(event.phase) }} ·
            {{ Number(event.retainedFlag) ? "历史保留消息" : "实时消息" }} ·
            {{ event.taskId ? "已关联任务" : "未用于确认任务" }}</template
          >
          <pre class="ota-json">{{ event.payload }}</pre>
        </el-collapse-item></el-collapse
      ></el-dialog
    >
  </div>
  <el-empty v-else description="仅管理员可访问 OTA 升级管理" />
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from "vue";
import { useStore } from "vuex";
import { ElMessage, ElMessageBox } from "element-plus";
import { Refresh, UploadFilled } from "@element-plus/icons-vue";
import * as api from "@/api/ota";
import {
  otaPermissions,
  otaLabel,
  otaTone,
  otaTime,
  canStartOta,
} from "@/utils/ota";

const store = useStore();
const role = computed(() => store.getters.userRole);
const permissions = computed(() => otaPermissions(role.value));
const loading = ref(false),
  busy = ref(false),
  error = ref("");
const settings = ref({}),
  releases = ref([]),
  deployments = ref([]),
  audit = ref([]);
const activeTab = ref("releases");
const published = computed(() =>
  releases.value.filter((r) => r.state === "PUBLISHED")
);
const readyToWrite = computed(
  () =>
    settings.value.available && settings.value.storageConfigured && !busy.value
);
const cards = computed(() => [
  { label: "固件版本", value: releases.value.length },
  { label: "已发布版本", value: published.value.length },
  {
    label: "进行中的任务",
    value: deployments.value.filter((r) =>
      ["RUNNING", "AWAITING_CONTINUE"].includes(r.state)
    ).length,
  },
  {
    label: "当前操作权限",
    value: permissions.value.operate ? "超级管理员" : "管理员",
  },
]);
let loadGeneration = 0;
async function load() {
  if (!permissions.value.manage) return;
  const generation = ++loadGeneration;
  loading.value = true;
  error.value = "";
  try {
    const config = await api.otaSettings();
    if (generation !== loadGeneration) return;
    settings.value = config;
    if (!config.available)
      throw new Error("OTA 数据库尚未配置，请联系维护人员");
    const [versions, tasks, records] = await Promise.all([
      api.otaReleases(),
      api.otaDeployments(),
      api.otaAudit(),
    ]);
    if (generation !== loadGeneration) return;
    releases.value = versions;
    deployments.value = tasks;
    audit.value = records;
    await loadDevices();
  } catch (failure) {
    if (generation === loadGeneration) error.value = failure.message;
  } finally {
    if (generation === loadGeneration) loading.value = false;
  }
}
async function action(work) {
  if (busy.value) return;
  busy.value = true;
  try {
    await work();
    await load();
  } catch (failure) {
    if (failure !== "cancel" && failure !== "close")
      ElMessage.error(failure.message || "操作失败");
  } finally {
    busy.value = false;
  }
}
const uploadVisible = ref(false),
  uploadForm = ref({});
function openUpload() {
  uploadForm.value = {
    releaseId: "",
    baselineVersion: "",
    gitCommit: "",
    notes: "",
    firmware: null,
    versionFile: null,
  };
  uploadVisible.value = true;
}
function chooseFile(event, name) {
  uploadForm.value[name] = event.target.files?.[0] || null;
}
async function submitUpload() {
  const f = uploadForm.value;
  if (
    !f.releaseId ||
    !f.baselineVersion ||
    !f.gitCommit ||
    !f.notes ||
    !f.firmware ||
    !f.versionFile
  ) {
    ElMessage.warning("请完整填写信息并选择两个对应文件");
    return;
  }
  if (
    f.firmware.name !== "wifi_station.bin" ||
    f.firmware.size > 5 * 1024 * 1024 ||
    f.versionFile.name !== "version.txt" ||
    f.versionFile.size > 128
  ) {
    ElMessage.warning(
      "请选择不超过5MiB的wifi_station.bin和不超过128字节的version.txt"
    );
    return;
  }
  await action(async () => {
    const form = new FormData();
    for (const [key, value] of Object.entries(f)) form.append(key, value);
    await api.otaUpload(form);
    uploadVisible.value = false;
    ElMessage.success("固件校验通过，已保存草稿");
  });
}
const releaseVisible = ref(false),
  selectedRelease = ref(null),
  manifestText = ref("");
async function viewRelease(row) {
  try {
    const manifest = await api.otaManifest(row.releaseId);
    selectedRelease.value = row;
    manifestText.value = JSON.stringify(manifest, null, 2);
    releaseVisible.value = true;
  } catch (failure) {
    ElMessage.error(failure.message);
  }
}
async function releaseAction(row, command) {
  const messages = {
    approve: "确认已核对固件来源和测试记录，审核此实验室联调版本？",
    publish: settings.value.preview
      ? "将生成本地预览文件，不会发布到公网，也不会触发设备。继续？"
      : "将发布不可覆盖的固件与升级清单。发布本身不会触发设备，继续？",
    withdraw: "撤回后不再发送后续设备；已开始的下载或升级无法可靠取消。继续？",
  };
  await action(async () => {
    await ElMessageBox.confirm(
      `${row.releaseId}：${messages[command]}`,
      "确认版本操作",
      { type: "warning" }
    );
    await api.otaReleaseAction(row.releaseId, command);
    ElMessage.success("版本状态已更新");
  });
}
const deviceRows = ref([]),
  deviceKeyword = ref(""),
  deviceLoading = ref(false);
let deviceGeneration = 0;
async function loadDevices() {
  const generation = ++deviceGeneration;
  deviceLoading.value = true;
  try {
    const rows = await api.otaDevices({ keyword: deviceKeyword.value });
    if (generation === deviceGeneration) deviceRows.value = rows;
  } catch (failure) {
    if (generation === deviceGeneration) error.value = failure.message;
  } finally {
    if (generation === deviceGeneration) deviceLoading.value = false;
  }
}
const taskVisible = ref(false),
  taskForm = ref({}),
  candidates = ref([]),
  candidateKeyword = ref(""),
  candidateLoading = ref(false),
  candidateTable = ref(null);
let candidateGeneration = 0;
function openTask() {
  taskForm.value = { releaseId: "", batchSize: 1, deviceIds: [] };
  candidates.value = [];
  candidateKeyword.value = "";
  taskVisible.value = true;
}
async function loadCandidates() {
  const generation = ++candidateGeneration;
  taskForm.value.deviceIds = [];
  candidateTable.value?.clearSelection();
  if (!taskForm.value.releaseId) {
    candidates.value = [];
    return;
  }
  candidateLoading.value = true;
  try {
    const rows = await api.otaDevices({
      keyword: candidateKeyword.value,
      releaseId: taskForm.value.releaseId,
    });
    if (generation === candidateGeneration) candidates.value = rows;
  } catch (failure) {
    if (generation === candidateGeneration) ElMessage.error(failure.message);
  } finally {
    if (generation === candidateGeneration) candidateLoading.value = false;
  }
}
function selectCandidates(rows) {
  taskForm.value.deviceIds = rows.map((r) => r.deviceId);
}
async function submitTask() {
  await action(async () => {
    await api.otaCreateDeployment(taskForm.value);
    taskVisible.value = false;
    activeTab.value = "tasks";
    ElMessage.success("任务已保存，尚未发送升级命令");
  });
}
const detailVisible = ref(false),
  taskDetail = ref(null);
let detailGeneration = 0;
async function viewTask(row) {
  const generation = ++detailGeneration;
  try {
    const detail = await api.otaDeployment(row.id);
    if (generation === detailGeneration) {
      taskDetail.value = detail;
      detailVisible.value = true;
    }
  } catch (failure) {
    ElMessage.error(failure.message);
  }
}
async function refreshTask() {
  if (taskDetail.value) await viewTask(taskDetail.value);
}
async function startTask(row) {
  await action(async () => {
    const detail = await api.otaDeployment(row.id);
    const ids = detail.tasks
      .filter((t) => t.phase === "PENDING")
      .slice(0, Number(detail.batchSize))
      .map((t) => t.deviceId);
    await ElMessageBox.confirm(
      `版本 ${row.releaseId}；本批机器码：${
        ids.join("、") || "无"
      }。服务器将复核公网文件与设备条件，然后实际发送升级命令。是否继续？`,
      "确认设备升级",
      { type: "warning", confirmButtonText: "确认下发本批" }
    );
    await api.otaDeploymentAction(row.id, "start");
    ElMessage.success("本批已进入下发流程，请查看设备进度");
    if (detailVisible.value) await refreshTask();
  });
}
async function pauseTask(row) {
  await action(async () => {
    await ElMessageBox.confirm(
      "暂停只停止尚未下发的设备，不能可靠取消已经开始的升级。确认暂停？",
      "暂停后续设备",
      { type: "warning" }
    );
    await api.otaDeploymentAction(row.id, "pause");
    ElMessage.success("后续设备已暂停");
    if (detailVisible.value) await refreshTask();
  });
}
const readinessVisible = ref(false),
  readinessId = ref(""),
  readinessForm = ref({});
function editReadiness(row) {
  readinessId.value = row.deviceId;
  readinessForm.value = {
    currentVersion: "",
    boardRevision: "",
    flashMiB: 16,
    c6Protocol: "",
    partitionSchema: 1,
    configSchema: 1,
    uniqueIdConfirmed: false,
    baselineReady: false,
    notes: "",
    ...row.readiness,
  };
  readinessVisible.value = true;
}
async function submitReadiness() {
  await action(async () => {
    await api.otaReadiness(readinessId.value, readinessForm.value);
    readinessVisible.value = false;
    ElMessage.success("设备条件已登记，开始升级前仍会检查实时状态");
  });
}
const eventsVisible = ref(false),
  eventsDevice = ref(""),
  eventRows = ref([]);
async function viewEvents(id) {
  try {
    const rows = await api.otaEvents(id);
    eventsDevice.value = id;
    eventRows.value = rows;
    eventsVisible.value = true;
  } catch (failure) {
    ElMessage.error(failure.message);
  }
}
const auditLabel = (value) =>
  ({
    UPLOAD: "上传固件",
    APPROVE_LAB: "审核联调版本",
    PUBLISH_BEGIN: "开始发布",
    PUBLISH: "发布版本",
    PUBLISH_ERROR: "发布异常",
    WITHDRAW: "撤回版本",
    DEVICE_READINESS: "登记设备条件",
    CREATE_DEPLOYMENT: "创建任务",
    START_BATCH: "下发本批",
    PAUSE: "人工暂停",
    DISPATCH_UNCERTAIN: "发送结果不明",
    TIMEOUT: "设备状态超时",
    CONDITIONS_CHANGED: "设备条件变化",
  }[value] || value);
let timer;
onMounted(() => {
  load();
  timer = setInterval(() => {
    if (detailVisible.value && !busy.value && !document.hidden) refreshTask();
  }, 10000);
});
onBeforeUnmount(() => {
  clearInterval(timer);
  loadGeneration++;
  deviceGeneration++;
  candidateGeneration++;
  detailGeneration++;
});
</script>

<style scoped>
.ota-page {
  color: #263e47;
}
.ota-heading,
.ota-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}
.ota-heading {
  margin-bottom: 20px;
}
.ota-heading h2 {
  margin: 0;
  display: flex;
  gap: 9px;
  align-items: center;
  font-size: 23px;
}
.ota-heading h2 .el-icon {
  color: #159780;
}
.ota-heading p {
  margin: 8px 0 0;
  color: #7a8990;
  font-size: 13px;
}
.ota-notice {
  margin-bottom: 16px;
}
.ota-cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 15px;
  margin: 20px 0;
}
.ota-card {
  padding: 20px 22px;
  border: 1px solid #e4eceb;
  border-radius: 10px;
  background: white;
}
.ota-card span {
  display: block;
  font-size: 13px;
  color: #7a8990;
  margin-bottom: 12px;
}
.ota-card strong {
  font-size: 25px;
  font-weight: 600;
  color: #147e70;
}
.ota-panel {
  background: white;
  padding: 18px 22px;
  border: 1px solid #e4eceb;
  border-radius: 10px;
}
.ota-toolbar {
  margin: 12px 0 20px;
}
.ota-toolbar > span {
  font-size: 13px;
  color: #71828a;
}
.ota-footnote {
  color: #7a8990;
  font-size: 12px;
  line-height: 1.8;
  margin: 16px 0 4px;
}
.ota-inline-note {
  margin: 0 10px;
  font-size: 12px;
  color: #7a8990;
}
.ota-wrap {
  word-break: break-all;
}
.ota-json {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  background: #f4f7f7;
  border-radius: 6px;
  padding: 16px;
  line-height: 1.65;
  max-height: 340px;
  overflow: auto;
  font-size: 12px;
}
.ota-blocker {
  font-size: 12px;
  color: #aa6b25;
}
.ota-good {
  font-size: 12px;
  color: #14866e;
}
@media (max-width: 1050px) {
  .ota-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .ota-toolbar {
    flex-wrap: wrap;
  }
}
</style>
